import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {ICurrency} from "../models/devise.model";
import {getPageableAsParams, Page, Pageable} from "../models/page.model";

@Injectable({
  providedIn: 'root',
})
export class DeviseService {
  private readonly apiUrl = 'operation/v1/devises';

  constructor(private readonly http: HttpClient) {}

  /**
   * Get all devises with optional pagination and sorting.
   * @param pageable The page number (0-based index).
   * @returns An observable of a page of ICurrencys.
   */
  getAllDevises(pageable: Pageable): Observable<Page<ICurrency>> {
    const params = getPageableAsParams(pageable);
    return this.http.get<Page<ICurrency>>(this.apiUrl, { params });
  }

  /**
   * Get a specific devise by its code.
   * @param code The code of the devise.
   * @returns An observable of the ICurrency.
   */
  getDeviseByCode(code: string): Observable<ICurrency> {
    let params = new HttpParams().set('code', code);
    return this.http.get<ICurrency>(`${this.apiUrl}/code`, { params });
  }

  /**
   * Create a new devise.
   * @param deviseDTO The ICurrency object.
   * @returns An observable of the created ICurrency.
   */
  createDevise(deviseDTO: ICurrency): Observable<ICurrency> {
    return this.http.post<ICurrency>(this.apiUrl, deviseDTO);
  }

  /**
   * Update an existing devise by its code.
   * @param code The code of the devise to update.
   * @param deviseDTO The updated ICurrency object.
   * @returns An observable of the updated ICurrency.
   */
  updateDevise(code: string, deviseDTO: ICurrency): Observable<ICurrency> {
    let params = new HttpParams().set('code', code);
    return this.http.put<ICurrency>(this.apiUrl, deviseDTO, { params });
  }

  /**
   * Delete (soft delete) a devise by its code.
   * @param code The code of the devise to delete.
   * @returns An observable of void.
   */
  deleteDevise(code: string): Observable<void> {
    let params = new HttpParams().set('code', code);
    return this.http.delete<void>(this.apiUrl, { params });
  }
}
